import React, { createContext, useContext, useRef, useState } from 'react';
import { blastApi } from '@flows/api/blast-api';
import type { FlowDetail } from '@flows/api/blast-api-types';
import { useWorkspace } from "@/context/workspace";

interface SelectedFlowContextValue {
  selectedFlowId: string | null;
  selectedFlow: FlowDetail | null;
  isLoadingFlow: boolean;
  flowError: string | null;
  selectFlow: (flowId: string) => Promise<void>;
  clearSelection: () => void;
  reset: () => void;
}

const SelectedFlowContext = createContext<SelectedFlowContextValue | undefined>(undefined);

export function FlowSelectionProvider({ children }: { children: React.ReactNode }) {
  // Cache and request sequencing
  const cacheRef = useRef(new Map<string, FlowDetail>());
  const requestSeqRef = useRef(0);

  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<FlowDetail | null>(null);
  const [isLoadingFlow, setIsLoadingFlow] = useState(false);
  const [flowError, setFlowError] = useState<string | null>(null);

  const selectFlow = async (flowId: string) => {
    setSelectedFlowId(flowId);
    setFlowError(null);

    // Serve from cache if available
    const cached = cacheRef.current.get(flowId);
    if (cached) {
      setSelectedFlow(cached);
      setIsLoadingFlow(false);
      return;
    }

    // Not cached: immediately clear the previous selection to avoid stale graph usage
    setSelectedFlow(null);
    setIsLoadingFlow(true);
    const seq = ++requestSeqRef.current;

    try {
      // Special handling for the test flow
      if (flowId === "executors/loop/fixed/increment-3") {
        const testFlow: FlowDetail = {
          "id": "executors/loop/fixed/increment-3",
          "title": "Fixed Loop: increment 3 times",
          "category": "Executors/Loop/Fixed",
          "tags": [
              "pins:required",
              "loop",
              "fixed",
              "increment"
          ],
          "defaults": {
              "initialInputs": {
                  "value": 10
              },
              "scheduler": {
                  "maxParallelism": 10
              }
          },
          "graph": {
              "nodes": [
                  {
                      "id": "start",
                      "type": "standard",
                      "data": {
                          "label": "Start",
                          "handler": "log",
                          "isStart": true,
                          "pins": {
                              "exec": {
                                  "in": [
                                      {
                                          "id": "in"
                                      }
                                  ],
                                  "out": [
                                      {
                                          "id": "next"
                                      }
                                  ]
                              },
                              "data": {
                                  "in": [
                                      {
                                          "id": "in"
                                      }
                                  ],
                                  "out": [
                                      {
                                          "id": "value",
                                          "type": "number"
                                      }
                                  ]
                              }
                          }
                      }
                  },
                  {
                      "id": "loop",
                      "type": "loop",
                      "data": {
                          "label": "Loop 3 times",
                          "iterations": 3,
                          "pins": {
                              "exec": {
                                  "in": [
                                      {
                                          "id": "in"
                                      }
                                  ],
                                  "out": [
                                      {
                                          "id": "body"
                                      },
                                      {
                                          "id": "done"
                                      }
                                  ]
                              },
                              "data": {
                                  "in": [
                                      {
                                          "id": "value",
                                          "type": "number"
                                      }
                                  ],
                                  "out": []
                              }
                          }
                      }
                  },
                  {
                      "id": "process",
                      "type": "standard",
                      "data": {
                          "label": "Increment",
                          "handler": "transform",
                          "operation": "increment",
                          "pins": {
                              "exec": {
                                  "in": [
                                      {
                                          "id": "in"
                                      }
                                  ],
                                  "out": [
                                      {
                                          "id": "next"
                                      }
                                  ]
                              },
                              "data": {
                                  "in": [
                                      {
                                          "id": "value",
                                          "type": "number",
                                          "required": true
                                      }
                                  ],
                                  "out": [
                                      {
                                          "id": "value",
                                          "type": "number"
                                      }
                                  ]
                              }
                          }
                      }
                  },
                  {
                      "id": "store-final",
                      "type": "standard",
                      "data": {
                          "label": "Store Final",
                          "handler": "store",
                          "key": "final",
                          "pins": {
                              "exec": {
                                  "in": [
                                      {
                                          "id": "in"
                                      }
                                  ],
                                  "out": [
                                      {
                                          "id": "next"
                                      }
                                  ]
                              },
                              "data": {
                                  "in": [
                                      {
                                          "id": "value",
                                          "type": "number"
                                      }
                                  ],
                                  "out": []
                              }
                          }
                      }
                  },
                  {
                      "id": "done",
                      "type": "standard",
                      "data": {
                          "label": "Done",
                          "handler": "log",
                          "pins": {
                              "exec": {
                                  "in": [
                                      {
                                          "id": "in"
                                      }
                                  ],
                                  "out": [
                                      {
                                          "id": "next"
                                      }
                                  ]
                              },
                              "data": {
                                  "in": [],
                                  "out": []
                              }
                          }
                      }
                  }
              ],
              "edges": [
                  {
                      "id": "x-start->loop",
                      "source": "start",
                      "target": "loop",
                      "data": {
                          "channel": "exec"
                      },
                      "sourceHandle": "next",
                      "targetHandle": "in"
                  },
                  {
                      "id": "x-loop.body->process",
                      "source": "loop",
                      "target": "process",
                      "data": {
                          "channel": "exec"
                      },
                      "sourceHandle": "body",
                      "targetHandle": "in"
                  },
                  {
                      "id": "x-process->store",
                      "source": "process",
                      "target": "store-final",
                      "data": {
                          "channel": "exec"
                      },
                      "sourceHandle": "next",
                      "targetHandle": "in"
                  },
                  {
                      "id": "x-store->done",
                      "source": "store-final",
                      "target": "done",
                      "data": {
                          "channel": "exec"
                      },
                      "sourceHandle": "next",
                      "targetHandle": "in"
                  },
                  {
                      "id": "d-start.value->process.value",
                      "source": "start",
                      "target": "process",
                      "data": {
                          "channel": "data",
                          "aggregate": "last"
                      },
                      "sourceHandle": "value",
                      "targetHandle": "value"
                  },
                  {
                      "id": "d-process.value->process.value",
                      "source": "process",
                      "target": "process",
                      "data": {
                          "channel": "data",
                          "aggregate": "last"
                      },
                      "sourceHandle": "value",
                      "targetHandle": "value"
                  },
                  {
                      "id": "d-process.value->loop.value",
                      "source": "process",
                      "target": "loop",
                      "data": {
                          "channel": "data",
                          "aggregate": "last"
                      },
                      "sourceHandle": "value",
                      "targetHandle": "value"
                  },
                  {
                      "id": "d-process.value->store.value",
                      "source": "process",
                      "target": "store-final",
                      "data": {
                          "channel": "data",
                          "aggregate": "last"
                      },
                      "sourceHandle": "value",
                      "targetHandle": "value"
                  }
              ]
          }
        };
        
        if (seq !== requestSeqRef.current) return;
        cacheRef.current.set(flowId, testFlow);
        setSelectedFlow(testFlow);
      } else {
        const detail = await blastApi.getFlowById(flowId, true);
        // Drop stale responses
        if (seq !== requestSeqRef.current) return;
  
        cacheRef.current.set(flowId, detail);
        setSelectedFlow(detail);
      }
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      setFlowError(e instanceof Error ? e.message : 'Failed to load flow');
      setSelectedFlow(null);
    } finally {
      if (seq === requestSeqRef.current) setIsLoadingFlow(false);
    }
  };

  const clearSelection = () => {
    setSelectedFlowId(null);
    setSelectedFlow(null);
    setFlowError(null);
    setIsLoadingFlow(false);
  };

  const reset = () => {
    // Clear any cached flows and reset to initial state
    cacheRef.current.clear();
    requestSeqRef.current = 0;
    clearSelection();
  };

  // Reset on workspace change
  const { wsChangeCount } = useWorkspace();
  React.useEffect(() => {
    reset();
  }, [wsChangeCount]);

  return (
    <SelectedFlowContext.Provider
      value={{ selectedFlowId, selectedFlow, isLoadingFlow, flowError, selectFlow, clearSelection, reset }}
    >
      {children}
    </SelectedFlowContext.Provider>
  );
}

export function useFlowSelection(): SelectedFlowContextValue {
  const ctx = useContext(SelectedFlowContext);
  if (!ctx) throw new Error('useFlowSelection must be used within FlowSelectionProvider');
  return ctx;
}
